import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { chatHeadInitials } from './staffChatMessages';

const COMPACT_MQ = '(max-width: 768px)';
const MAX_VISIBLE_HEADS = 3;

export type MessengerThread = {
  id: string;
  kind: 'tele' | 'support';
  name: string;
  subtitle: string;
  unread: number;
  expanded: boolean;
};

type HubValue = {
  isCompact: boolean;
  threads: MessengerThread[];
  pickerOpen: boolean;
  setPickerOpen: (open: boolean) => void;
  switchTo: (id: string) => void;
};

const HubContext = createContext<HubValue | null>(null);

function useCompactMessenger(): boolean {
  const [compact, setCompact] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(COMPACT_MQ).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(COMPACT_MQ);
    const onChange = () => setCompact(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return compact;
}

function HeadButton({
  thread,
  onClick,
}: {
  thread: MessengerThread;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`staff-messenger-head ${thread.kind === 'support' ? 'staff-messenger-head--support' : ''} ${thread.unread > 0 ? 'staff-messenger-head--unread' : ''}`}
      onClick={onClick}
      title={thread.name}
      aria-label={
        thread.unread > 0
          ? `Open chat with ${thread.name}, ${thread.unread} unread`
          : `Open chat with ${thread.name}`
      }
    >
      <span className="staff-messenger-head__avatar" aria-hidden>
        {chatHeadInitials(thread.name)}
      </span>
      {thread.unread > 0 ? (
        <span className="staff-messenger-head__badge">{thread.unread > 9 ? '9+' : thread.unread}</span>
      ) : null}
    </button>
  );
}

function MobileHeadsAndPicker({
  threads,
  pickerOpen,
  setPickerOpen,
  switchTo,
}: {
  threads: MessengerThread[];
  pickerOpen: boolean;
  setPickerOpen: (open: boolean) => void;
  switchTo: (id: string) => void;
}) {
  const anyExpanded = threads.some((t) => t.expanded);
  const minimized = threads.filter((t) => !t.expanded);
  const ranked = [
    ...minimized.filter((t) => t.unread > 0),
    ...minimized.filter((t) => t.unread <= 0),
  ];
  const overflowCount = ranked.length > MAX_VISIBLE_HEADS ? ranked.length - (MAX_VISIBLE_HEADS - 1) : 0;
  const visibleHeads = overflowCount > 0 ? ranked.slice(0, MAX_VISIBLE_HEADS - 1) : ranked;
  const overflowUnread =
    overflowCount > 0
      ? ranked.slice(MAX_VISIBLE_HEADS - 1).reduce((sum, t) => sum + t.unread, 0)
      : 0;

  if (pickerOpen) {
    return (
      <div className="staff-messenger-picker" role="dialog" aria-labelledby="staff-messenger-picker-title">
        <div className="staff-messenger-picker__bar">
          <h2 id="staff-messenger-picker-title" className="staff-messenger-picker__title">
            Open chats
            <span className="staff-messenger-picker__count">{threads.length}</span>
          </h2>
          <button type="button" className="staff-messenger-picker__done" onClick={() => setPickerOpen(false)}>
            Done
          </button>
        </div>
        {threads.length === 0 ? (
          <p className="staff-messenger-picker__empty">No open chats.</p>
        ) : (
          <ul className="staff-messenger-picker__list">
            {threads.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  className={`staff-messenger-picker__row ${t.expanded ? 'is-active' : ''} ${t.kind === 'support' ? 'is-support' : ''}`}
                  onClick={() => switchTo(t.id)}
                >
                  <span
                    className={`staff-messenger-head__avatar ${t.kind === 'support' ? 'staff-messenger-head__avatar--support' : ''}`}
                    aria-hidden
                  >
                    {chatHeadInitials(t.name)}
                  </span>
                  <span className="staff-messenger-picker__meta">
                    <strong>{t.name}</strong>
                    <span>{t.kind === 'support' ? `Support · ${t.subtitle}` : t.subtitle}</span>
                  </span>
                  {t.unread > 0 ? (
                    <span className="staff-messenger-picker__unread">{t.unread > 9 ? '9+' : t.unread}</span>
                  ) : t.expanded ? (
                    <span className="staff-messenger-picker__now">Open</span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  if (anyExpanded || ranked.length === 0) {
    return null;
  }

  return (
    <div className="staff-messenger-heads" aria-label="Minimized chats">
      {overflowCount > 0 ? (
        <button
          type="button"
          className={`staff-messenger-head staff-messenger-head--more ${overflowUnread > 0 ? 'staff-messenger-head--unread' : ''}`}
          onClick={() => setPickerOpen(true)}
          aria-label={`Show ${overflowCount} more chats`}
          title={`${overflowCount} more chats`}
        >
          <span className="staff-messenger-head__avatar" aria-hidden>
            +{overflowCount}
          </span>
          {overflowUnread > 0 ? (
            <span className="staff-messenger-head__badge">{overflowUnread > 9 ? '9+' : overflowUnread}</span>
          ) : null}
        </button>
      ) : ranked.length > 1 ? (
        <button
          type="button"
          className="staff-messenger-head staff-messenger-head--list"
          onClick={() => setPickerOpen(true)}
          aria-label="Show all open chats"
          title="All open chats"
        >
          <span className="staff-messenger-head__avatar" aria-hidden>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M4 6h16v2H4V6zm0 5h16v2H4v-2zm0 5h16v2H4v-2z" />
            </svg>
          </span>
        </button>
      ) : null}
      {visibleHeads.map((t) => (
        <HeadButton key={t.id} thread={t} onClick={() => switchTo(t.id)} />
      ))}
    </div>
  );
}

/** Avatar row inside an open mobile chat — tap to jump without going back to the queue. */
export function StaffMessengerThreadSwitcher() {
  const hub = useContext(HubContext);
  if (!hub?.isCompact || hub.threads.length < 2) return null;
  return (
    <div className="staff-messenger-thread-switcher" aria-label="Switch open chat">
      {hub.threads.map((t) => (
        <button
          key={t.id}
          type="button"
          className={`staff-messenger-thread-switcher__item ${t.expanded ? 'is-active' : ''} ${t.kind === 'support' ? 'is-support' : ''}`}
          onClick={() => hub.switchTo(t.id)}
          title={t.name}
          aria-current={t.expanded ? 'true' : undefined}
          aria-label={t.unread > 0 ? `${t.name}, ${t.unread} unread` : t.name}
        >
          <span aria-hidden>{chatHeadInitials(t.name)}</span>
          {t.unread > 0 ? <i>{t.unread > 9 ? '9+' : t.unread}</i> : null}
        </button>
      ))}
      <button
        type="button"
        className="staff-messenger-thread-switcher__all"
        onClick={() => hub.setPickerOpen(true)}
      >
        All
      </button>
    </div>
  );
}

export function StaffMessengerHubProvider({
  threads,
  onSwitch,
  children,
}: {
  threads: MessengerThread[];
  onSwitch: (id: string) => void;
  children: React.ReactNode;
}) {
  const isCompact = useCompactMessenger();
  const [pickerOpen, setPickerOpen] = useState(false);

  const switchTo = useCallback(
    (id: string) => {
      setPickerOpen(false);
      onSwitch(id);
    },
    [onSwitch],
  );

  const value = useMemo(
    () => ({ isCompact, threads, pickerOpen, setPickerOpen, switchTo }),
    [isCompact, threads, pickerOpen, switchTo],
  );

  return (
    <HubContext.Provider value={value}>
      {children}
      {isCompact ? (
        <MobileHeadsAndPicker
          threads={threads}
          pickerOpen={pickerOpen}
          setPickerOpen={setPickerOpen}
          switchTo={switchTo}
        />
      ) : null}
    </HubContext.Provider>
  );
}
